import React, { useState, useRef, useEffect } from 'react';
import { Globe2, Play, Trash2, RefreshCw, Crown, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewsTicker from './NewsTicker';
import PremiumUpgrade from './PremiumUpgrade';
import GameLimitTimer from './GameLimitTimer';
import AdminVIPModal from './AdminVIPModal';
import AdminInvitesModal from './AdminInvitesModal';
import { playConsoleClick } from '../utils/sounds';

function GameLobby({ onStartGame, existingGames, onLoadGame, onDeleteGame, isLoading }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [gameName, setGameName] = useState('');
  const [playerCountry, setPlayerCountry] = useState('USA');
  const [showNameError, setShowNameError] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showInvitesModal, setShowInvitesModal] = useState(false);
  const [limitStatus, setLimitStatus] = useState(null);
  
  // Fun placeholder suggestions - light-hearted & thematic
  const getPlaceholder = () => {
    const isFirstGame = !existingGames || existingGames.length === 0;
    
    if (isFirstGame) {
      return "My First Game";
    }
    
    // Rotating satirical placeholders for returning players
    const suggestions = [
      "Victory Only",
      "World Conqueror",
      "Total Domination",
      "Nuclear Diplomacy",
      "Peace Through Nukes",
      "Global Takeover",
      "Supreme Leader",
      "Empire Builder",
      "Master Strategist",
      "Apocalypse Now",
      "Totally Peaceful",
      "Friendly Missiles",
      "Diplomatic Nukes"
    ];
    
    // Rotate based on number of existing games
    return suggestions[existingGames.length % suggestions.length];
  };
  
  // Admin emails that can access VIP management
  const ADMIN_EMAILS = ['kajbjorlin@gmail.com'];
  const isAdmin = ADMIN_EMAILS.includes(user?.email);
  
  // Preload keyboard click sound
  const keyboardAudioRef = useRef(null);
  const [musicStarted, setMusicStarted] = useState(false);
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);
  const titleMusicRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch game limit status on mount
  useEffect(() => {
    const fetchLimitStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/game-limits/status`, {
          headers: {
            'X-User-Email': user.email
          }
        });
        if (response.ok) {
          const data = await response.json();
          setLimitStatus(data);
        }
      } catch (error) {
        console.error('Error fetching limit status:', error);
      }
    };

    if (user?.email) {
      fetchLimitStatus();
    }
  }, [user, API_URL]);
  
  useEffect(() => {
    // Create and preload the audio element
    keyboardAudioRef.current = new Audio('/assets/audio/keyboard-click.wav');
    keyboardAudioRef.current.volume = 1.0;
    keyboardAudioRef.current.load();
    
    // Create title music using DOM approach
    const titleMusic = document.createElement('audio');
    titleMusic.id = 'title-music';
    titleMusic.src = '/assets/audio/ymca-title-song.mp3';
    titleMusic.volume = 0.1; // 10% volume - same as main game background music
    titleMusic.loop = true;
    titleMusic.preload = 'auto';
    
    titleMusicRef.current = titleMusic;
    
    // Add event listener for actual playing
    titleMusic.addEventListener('playing', () => {
      console.log('🎵 ✅✅✅ MUSIC IS NOW ACTUALLY PLAYING!');
      setMusicStarted(true);
      setShowMusicPrompt(false); // Hide prompt when music starts
    });
    
    titleMusic.addEventListener('error', (e) => console.error('🎵 ERROR:', e));
    
    document.body.appendChild(titleMusic);
    console.log('🎵 Title music element created');
    
    // STRATEGY: Play music when background video starts OR on any user interaction
    const tryPlayMusic = () => {
      if (!musicStarted && titleMusicRef.current) {
        console.log('🎵 Attempting to play music...');
        titleMusicRef.current.play()
          .then(() => {
            console.log('🎵 Play request accepted');
            setShowMusicPrompt(false);
          })
          .catch(err => {
            console.error('🎵 Play failed:', err.message);
            // Show prompt if autoplay blocked
            if (err.name === 'NotAllowedError') {
              console.log('🎵 Showing music prompt - user needs to interact');
              setShowMusicPrompt(true);
            }
          });
      }
    };
    
    // Wait for any video on the page to start playing
    const videos = document.querySelectorAll('video');
    console.log(`🎵 Found ${videos.length} video elements`);
    
    videos.forEach((video, index) => {
      video.addEventListener('play', () => {
        console.log(`🎵 Video ${index} started playing - triggering music!`);
        tryPlayMusic();
      });
      video.addEventListener('loadeddata', () => {
        console.log(`🎵 Video ${index} loaded - trying music`);
        tryPlayMusic();
      });
    });
    
    // Try after a delay (navigation counts as interaction)
    setTimeout(() => {
      console.log('🎵 Delayed attempt (navigation/video should enable this)');
      tryPlayMusic();
    }, 800);
    
    // Fallback: ANY user interaction (click, keypress, tap)
    const handleInteraction = (e) => {
      console.log('🎵 User interaction detected:', e.type);
      tryPlayMusic();
    };
    
    document.addEventListener('click', handleInteraction, { capture: true });
    document.addEventListener('keydown', handleInteraction, { capture: true, once: true });
    document.addEventListener('touchstart', handleInteraction, { capture: true, once: true });
    
    // Cleanup
    return () => {
      document.removeEventListener('click', handleInteraction, { capture: true });
      document.removeEventListener('keydown', handleInteraction, { capture: true });
      document.removeEventListener('touchstart', handleInteraction, { capture: true });
      if (titleMusicRef.current) {
        titleMusicRef.current.pause();
        titleMusicRef.current.remove();
      }
    };
  }, [musicStarted]);

  // Keyboard click sound handler
  const playKeyboardClick = () => {
    if (keyboardAudioRef.current) {
      try {
        // Clone and play to allow rapid successive plays
        const audio = keyboardAudioRef.current.cloneNode();
        audio.volume = 1.0; // 100% volume - loud and clear!
        audio.play().catch(err => {
          console.debug('Keyboard click sound blocked:', err);
        });
      } catch (err) {
        console.debug('Keyboard click audio error:', err);
      }
    }
  };

  const handleGameNameChange = (e) => {
    const newValue = e.target.value;
    
    // Only play sound if a character was added (not deleted)
    if (newValue.length > gameName.length) {
      playKeyboardClick();
    }
    
    setGameName(newValue);
    if (showNameError) setShowNameError(false);
  };

  const countries = [
    { code: 'USA', name: 'United States', color: '#3b82f6' },
    { code: 'RUS', name: 'Russia', color: '#ef4444' },
    { code: 'CHN', name: 'China', color: '#f59e0b' },
    { code: 'EU', name: 'European Union', color: '#22c55e' },
    { code: 'NK', name: 'North Korea', color: '#a855f7' },
  ];

  const handleStart = () => {
    // Check if user can start a game (game limits)
    if (limitStatus && !limitStatus.can_play && !limitStatus.is_premium) {
      // User is at limit - show premium modal
      setShowPremiumModal(true);
      return;
    }

    if (gameName.trim()) {
      // Play hydraulic door sound
      try {
        const doorSound = new Audio('/assets/audio/hydraulic-door.mp3');
        doorSound.volume = 0.6; // 60% volume for impactful sound
        doorSound.play().catch(err => console.debug('Door sound blocked:', err));
      } catch (err) {
        console.debug('Door sound error:', err);
      }
      
      // Start fade-out transition
      setIsTransitioning(true);
      
      // Wait 2 seconds to show the background animation, then start game
      setTimeout(() => {
        onStartGame(gameName.trim(), playerCountry);
      }, 2000);
    } else {
      // Show error tooltip and highlight
      setShowNameError(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowNameError(false);
      }, 3000);
    }
  };

  const handleLoadGame = (gameId) => {
    // Start fade-out transition
    setIsTransitioning(true);
    
    // Wait 2 seconds to show the background animation, then load game
    setTimeout(() => {
      onLoadGame(gameId);
    }, 2000);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Animated Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="fixed inset-0 w-full h-full object-cover"
          style={{ 
            zIndex: 0,
            filter: 'brightness(0.4)' // Darken video for readability
          }}
        >
          <source src="/assets/usa-missile-ride.mp4" type="video/mp4" />
        </video>
        
        {/* Music Prompt - appears if autoplay blocked */}
        {showMusicPrompt && (
          <div 
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg animate-pulse cursor-pointer"
            onClick={() => {
              if (titleMusicRef.current) {
                titleMusicRef.current.play()
                  .then(() => setShowMusicPrompt(false))
                  .catch(err => console.error('Music play failed:', err));
              }
            }}
          >
            <p className="text-white font-bold flex items-center gap-2">
              🎵 Click anywhere to start music
            </p>
          </div>
        )}
        
        {/* Dark overlay for better text readability - fade out during auth transition */}
        <div 
          className={`fixed inset-0 bg-black/50 transition-opacity duration-1000 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ zIndex: 1 }} 
        />
        
        {/* Content - fade out during auth transition */}
        <div 
          className={`relative z-10 bg-slate-800/90 backdrop-blur-sm p-12 rounded-lg shadow-2xl max-w-2xl border border-red-500/30 text-center transition-opacity duration-1000 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <h1 className="text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500">
            🌍 Global Thermo III
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            A turn-based strategy game of global domination
          </p>
          
          <div className="bg-slate-900/50 p-6 rounded-lg mb-8 border border-slate-700">
            <p className="text-lg text-slate-200 mb-4">
              ⚠️ <strong>Authentication Required</strong>
            </p>
            <p className="text-slate-400">
              You must be logged in to command your nation and play Global Thermo III.
            </p>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                playConsoleClick();
                
                // Play hydraulic door sound
                try {
                  const doorSound = new Audio('/assets/audio/hydraulic-door.mp3');
                  doorSound.volume = 0.6;
                  doorSound.play().catch(err => console.debug('Door sound blocked:', err));
                } catch (err) {
                  console.debug('Door sound error:', err);
                }
                
                // Start cinematic transition
                setIsTransitioning(true);
                
                // Wait 3 seconds to show background, then navigate
                setTimeout(() => {
                  navigate('/login');
                }, 3000);
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-8 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-lg"
            >
              🔐 Login
            </button>
            <button
              onClick={() => {
                playConsoleClick();
                
                // Play hydraulic door sound
                try {
                  const doorSound = new Audio('/assets/audio/hydraulic-door.mp3');
                  doorSound.volume = 0.6;
                  doorSound.play().catch(err => console.debug('Door sound blocked:', err));
                } catch (err) {
                  console.debug('Door sound error:', err);
                }
                
                // Start cinematic transition
                setIsTransitioning(true);
                
                // Wait 3 seconds to show background, then navigate
                setTimeout(() => {
                  navigate('/signup');
                }, 3000);
              }}
              className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-4 px-8 rounded-lg hover:from-green-700 hover:to-green-800 transition-all text-lg"
            >
              ✨ Create Account
            </button>
          </div>

          <p className="text-slate-500 mt-6 text-sm">
            New players: Create an account to start your campaign for world domination!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* News Ticker */}
      <NewsTicker />
      
      {/* Animated Video Background - brighten during transition */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className={`fixed inset-0 w-full h-full object-cover transition-all duration-1000`}
        style={{ 
          zIndex: 0,
          filter: isTransitioning ? 'brightness(0.8)' : 'brightness(0.4)'
        }}
      >
        <source src="/assets/nkorea-missile-ride.mp4" type="video/mp4" />
      </video>
      
      {/* Dark overlay for better text readability - fade out during transition */}
      <div 
        className={`fixed inset-0 bg-black/40 transition-opacity duration-1000 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{ zIndex: 1 }} 
      />
      
      {/* Content - fade out during transition */}
      <div 
        className={`relative flex items-center justify-center p-4 transition-opacity duration-1000 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{ zIndex: 2, minHeight: '100vh', marginTop: '48px' }}
      >
      <div className="bunker-panel p-8 w-full max-w-lg relative">
        {/* User Info Row */}
        <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-700">
          <div className="flex-1">
            <p className="text-xs text-muted uppercase">Commander</p>
            <div className="flex items-center gap-2">
              <p className="text-amber font-mono">{user.email}</p>
              
              {/* VIP Badge - Shows for premium users */}
              {limitStatus?.is_premium && (
                <div 
                  className="inline-flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide shadow-lg animate-pulse"
                  style={{
                    boxShadow: '0 0 10px rgba(234, 179, 8, 0.6), 0 0 20px rgba(234, 179, 8, 0.3)'
                  }}
                  title="VIP Premium Member"
                >
                  <Crown className="w-3 h-3" />
                  VIP
                </div>
              )}
            </div>
          </div>
          
          {/* Buttons in vertical stack to avoid overlap */}
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-2">
              {/* Admin VIP Management Button (only for admins) */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      playConsoleClick();
                      setShowAdminModal(true);
                    }}
                    className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white font-bold text-xs px-3 py-1 rounded uppercase tracking-wide transition-all flex items-center gap-1"
                    title="VIP Management"
                  >
                    <Crown className="w-3 h-3" />
                    Admin VIP
                  </button>
                  
                  <button
                    onClick={() => {
                      playConsoleClick();
                      setShowInvitesModal(true);
                    }}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold text-xs px-3 py-1 rounded uppercase tracking-wide transition-all flex items-center gap-1"
                    title="Send Beta Invites"
                  >
                    <Mail className="w-3 h-3" />
                    Invites
                  </button>
                </>
              )}
              
              <button
                onClick={() => {
                  playConsoleClick();
                  setShowPremiumModal(true);
                }}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold text-xs px-3 py-1 rounded uppercase tracking-wide transition-all flex items-center gap-1"
              >
                <Crown className="w-3 h-3" />
                Premium
              </button>
            </div>
            
            <button
              onClick={() => {
                playConsoleClick();
                handleLogout();
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-mono text-xs px-3 py-1 rounded uppercase tracking-wide transition-all w-full"
            >
              Logout
            </button>
          </div>
        </div>

        {/* BETA Stamp - Moved below buttons */}
        <div className="absolute top-24 right-8 pointer-events-none" style={{ zIndex: 10 }}>
          <div className="beta-stamp">
            BETA
          </div>
        </div>

        {/* Game Limit Timer */}
        <GameLimitTimer 
          userEmail={user.email}
          onUpgradeClick={() => setShowPremiumModal(true)}
        />

        {/* Title */}
        <div className="text-center mb-8">
          <Globe2 className="w-16 h-16 mx-auto mb-4 text-amber" />
          <h1 className="font-heading text-4xl tracking-wider text-amber mb-2">GLOBAL THERMO III</h1>
          <p className="text-muted font-mono text-sm uppercase tracking-widest">Turn-Based Global Strategy</p>
        </div>

        {/* New Game Form */}
        <div className="space-y-4 mb-8">
          <div className="relative">
            <label className="block text-xs text-muted uppercase tracking-widest mb-2">GAME NAME</label>
            <input
              type="text"
              value={gameName}
              onChange={handleGameNameChange}
              placeholder={getPlaceholder()}
              className={`w-full bg-neutral-900 border rounded px-4 py-3 font-mono text-white placeholder-neutral-600 focus:outline-none transition-all ${
                showNameError 
                  ? 'border-red-500 border-2 animate-pulse' 
                  : 'border-neutral-700 focus:border-amber'
              }`}
              data-testid="game-name-input"
            />
            
            {/* Error Tooltip */}
            {showNameError && (
              <div 
                className="absolute left-0 -bottom-12 bg-red-600 text-white px-4 py-2 rounded-lg border-2 border-red-400 shadow-lg z-50 animate-bounce"
                style={{
                  fontFamily: "'Orbitron', sans-serif",
                  textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⚠️</span>
                  <span className="text-sm font-bold uppercase tracking-wide">
                    Please Enter a Mission Code Name!
                  </span>
                </div>
                {/* Arrow pointing up */}
                <div className="absolute -top-2 left-8 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-red-600"></div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-muted uppercase tracking-widest mb-2">SELECT YOUR NATION</label>
            <div className="grid grid-cols-5 gap-2">
              {countries.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setPlayerCountry(c.code)}
                  className={`p-3 rounded border transition-all font-mono text-xs
                    ${playerCountry === c.code
                      ? 'border-2'
                      : 'border-neutral-700 hover:border-neutral-500'}
                  `}
                  style={{
                    borderColor: playerCountry === c.code ? c.color : undefined,
                    backgroundColor: playerCountry === c.code ? `${c.color}20` : 'transparent'
                  }}
                  data-testid={`country-select-${c.code}`}
                >
                  <div style={{ color: c.color }}>{c.code}</div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => {
              playConsoleClick(0.5);
              handleStart();
            }}
            disabled={isLoading || (limitStatus && !limitStatus.can_play && !limitStatus.is_premium)}
            className={`w-full py-4 flex items-center justify-center gap-2 ${
              limitStatus && !limitStatus.can_play && !limitStatus.is_premium
                ? 'bg-gray-600 cursor-not-allowed opacity-50'
                : 'launch-button'
            }`}
            data-testid="start-game-btn"
          >
            {isLoading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : limitStatus && !limitStatus.can_play && !limitStatus.is_premium ? (
              <>
                <span className="text-red-400">⏱️ WAITING FOR NEXT GAME</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                LAUNCH NEW GAME
              </>
            )}
          </button>
          
          {/* Copyright Notice */}
          <div className="text-center mt-4 text-xs text-neutral-500">
            © 2026 Beta by Kaj
          </div>
        </div>

        {/* Existing Games — sorted by most recently played first so users
            can find their active save without scrolling. */}
        {existingGames.length > 0 && (
          <div>
            <div className="text-xs text-muted uppercase tracking-widest mb-3">SAVED OPERATIONS</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {[...existingGames]
                .sort((a, b) => {
                  const ta = new Date(a.updated_at || a.created_at || 0).getTime();
                  const tb = new Date(b.updated_at || b.created_at || 0).getTime();
                  return tb - ta;
                })
                .map((game) => {
                  // Format date as "5/24/26 1:03pm" — compact for narrow
                  // mobile rows but still includes the year and time.
                  let dateLabel = '';
                  const raw = game.updated_at || game.created_at;
                  if (raw) {
                    const d = new Date(raw);
                    if (!isNaN(d.getTime())) {
                      const mm = d.getMonth() + 1;
                      const dd = d.getDate();
                      const yy = String(d.getFullYear()).slice(-2);
                      let hours = d.getHours();
                      const minutes = String(d.getMinutes()).padStart(2, '0');
                      const ampm = hours >= 12 ? 'pm' : 'am';
                      hours = hours % 12;
                      if (hours === 0) hours = 12;
                      dateLabel = `${mm}/${dd}/${yy} ${hours}:${minutes}${ampm}`;
                    }
                  }
                  return (
                    <div
                      key={game.id}
                      className="flex items-center justify-between bg-neutral-900/50 border border-neutral-800 rounded p-3"
                    >
                      <div className="min-w-0 flex-1 mr-2">
                        <div className="font-mono text-sm text-white truncate">{game.name}</div>
                        <div className="text-xs text-muted flex flex-wrap items-center gap-x-2">
                          <span>Turn {game.current_turn} • {game.phase}</span>
                          {dateLabel && (
                            <span className="text-amber-400/80" data-testid={`saved-game-date-${game.id}`}>
                              {dateLabel}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => {
                            playConsoleClick();
                            handleLoadGame(game.id);
                          }}
                          className="tactical-button px-3 py-1 text-xs"
                          data-testid={`load-game-${game.id}`}
                        >
                          LOAD
                        </button>
                        <button
                          onClick={() => {
                            playConsoleClick();
                            onDeleteGame(game.id);
                          }}
                          className="p-1 text-red-500 hover:text-red-400"
                          data-testid={`delete-game-${game.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
      </div>
      
      {/* Premium Upgrade Modal */}
      {showPremiumModal && (
        <PremiumUpgrade 
          userEmail={user.email} 
          onClose={() => setShowPremiumModal(false)}
        />
      )}
      
      {/* Admin VIP Management Modal */}
      {showAdminModal && (
        <AdminVIPModal
          user={user}
          onClose={() => setShowAdminModal(false)}
        />
      )}
      
      {/* Admin Invites Modal */}
      {showInvitesModal && (
        <AdminInvitesModal
          onClose={() => setShowInvitesModal(false)}
        />
      )}
    </div>
  );
}

export default GameLobby;
